import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import {
  Building2,
  FileText,
  MapPin,
  Calendar,
  ArrowRight,
  KeyRound,
  Layers,
  Download,
} from "lucide-react";
import { api } from "@/lib/api";
import { fileUrl, formatArea, formatFileSize, formatPriceHuf } from "@/lib/format";
import { arePricesHidden, unitDisplayPriceHuf } from "@shared/pricing";
import { isMaintenanceActive } from "@shared/maintenance";
import { useIsAuthenticated } from "@/lib/auth";
import { useDocumentTitle } from "@/lib/use-document-title";
import { DynamicForm } from "@/components/dynamic-form";
import { ScrollReveal } from "@/components/scroll-reveal";
import { ImageGallery } from "@/components/image-gallery";
import { UnitFilter } from "@/components/unit-filter";
import { BuildingSection } from "@/components/building-section";
import { MortgageAdvisorBox } from "@/components/mortgage-advisor";
import { Neighborhood } from "@/components/neighborhood";
import { RequestQuoteLink, unitQuoteHref } from "@/components/request-quote";
import { ComingSoonPanel } from "@/components/coming-soon-panel";
import {
  UNIT_STATUS_BADGE,
  UNIT_STATUS_LABEL,
  unitSlug,
} from "@/lib/unit-helpers";
import type {
  Apartment,
  Floor,
  Project,
  ProjectUnit,
  TechnicalPdf,
  TechnicalSpec,
} from "@shared/schema";

type FloorWithApartments = Floor & { apartments: Apartment[] };
type Detail = {
  project: Project;
  units: ProjectUnit[];
  floors?: FloorWithApartments[];
};

const STATUS_LABEL: Record<string, string> = {
  planning: "Tervezés alatt",
  construction: "Kivitelezés alatt",
  sales: "Értékesítés alatt",
  completed: "Befejezett",
};

export function ProjectDetailPage({ slug }: { slug: string }) {
  const isAdmin = useIsAuthenticated();
  const q = useQuery({
    queryKey: ["project", slug],
    queryFn: () => api<Detail>(`/api/projects/${slug}`, { withAuth: false }),
  });

  useDocumentTitle(q.data?.project.name);

  if (q.isLoading) {
    return (
      <div className="container py-24 text-brand-500">Betöltés...</div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <div className="container py-24 text-center">
        <h1 className="section-title text-3xl">A projekt nem található</h1>
        <Link href="/projektek" className="btn-primary mt-6 inline-flex">
          ← Vissza a projektekhez
        </Link>
      </div>
    );
  }

  const { project, units } = q.data;
  // Ár-elrejtés projekt-szinten: törölt referencia árak VAGY kézi kapcsoló →
  // a lakásoknál az ár helyén „Kérjen ajánlatot" áll (shared/pricing.ts).
  const pricesHidden = arePricesHidden(project.pricingDefaults);
  // Projekt-szintű „Hamarosan”: a szerver már üres teasert küld a látogatónak
  // (nincs leírás / galéria / lakás), itt csak a panelt tesszük a helyére. Az
  // admin a valódi tartalmat kapja — neki figyelmeztető sor jelzi az állapotot.
  const comingSoon = isMaintenanceActive(project.maintenance);
  const floors = q.data.floors ?? [];
  const sortedUnits = [...units].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label),
  );

  return (
    <div className="bg-white">
      {/* Hero with cover */}
      <section
        data-preview-section="basics"
        className="relative bg-brand-900 h-[60vh] min-h-[420px] overflow-hidden"
      >
        {project.coverImageKey ? (
          <img
            src={fileUrl(project.coverImageKey)}
            alt={project.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-white/20">
            <Building2 size={160} strokeWidth={0.6} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/55" />

        <div className="relative z-10 container h-full flex flex-col justify-end pb-12">
          <Link
            href="/projektek"
            className="text-xs uppercase tracking-widish font-semibold text-white/80 hover:text-accent transition-colors mb-4"
          >
            ← Vissza a projektekhez
          </Link>
          <div className="text-xs font-semibold uppercase tracking-widish text-accent mb-3">
            {STATUS_LABEL[project.status]}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-normal uppercase tracking-ultrawide text-white text-shadow-strong">
            {project.name}
          </h1>
          {project.shortDescription && (
            <p className="mt-4 text-base lg:text-lg text-white/85 max-w-2xl">
              {project.shortDescription}
            </p>
          )}
        </div>
      </section>

      <div className="container py-12 lg:py-16 grid lg:grid-cols-3 gap-12 max-w-full overflow-hidden">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-14 min-w-0 max-w-full">
          {comingSoon &&
            (isAdmin ? (
              <div className="border border-amber-300 bg-amber-50 text-amber-800 text-sm px-4 py-3">
                Ez a projekt „Hamarosan” módban van — a látogatók nem látják a
                tartalmát (leírás, galéria, lakások, árak), Ön adminként igen.
              </div>
            ) : (
              <ComingSoonPanel maintenance={project.maintenance} />
            ))}

          {/* Description */}
          {project.description && (
            <ScrollReveal>
              <p className="text-brand-600 leading-relaxed whitespace-pre-line">
                {project.description}
              </p>
            </ScrollReveal>
          )}

          {/* Gallery */}
          {project.gallery.length > 0 && (
            <div data-preview-section="gallery" className="scroll-mt-24">
              <ScrollReveal>
                <h2 className="text-lg font-semibold uppercase tracking-widish text-brand-700 mb-5">
                  Galéria
                </h2>
                <ImageGallery items={project.gallery} />
              </ScrollReveal>
            </div>
          )}

          {/* Floorplans */}
          {project.floorplans.length > 0 && (
            <div data-preview-section="floorplans" className="scroll-mt-24">
            <ScrollReveal>
              <h2 className="text-lg font-semibold uppercase tracking-widish text-brand-700 mb-4">
                Alaprajzok
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {project.floorplans.map((fp) => (
                  <a
                    key={fp.key}
                    href={fileUrl(fp.key)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="card p-4 flex items-center gap-3 hover:bg-brand-50 transition-colors group"
                  >
                    <FileText size={24} className="text-accent shrink-0" />
                    <div className="flex-1 text-sm">
                      <div className="font-medium text-brand-700 group-hover:text-accent transition-colors">
                        {fp.label}
                      </div>
                      <div className="text-xs text-brand-400">{fp.mimeType}</div>
                    </div>
                  </a>
                ))}
              </div>
            </ScrollReveal>
            </div>
          )}

          {/* Műszaki tartalom — PDF dokumentum + strukturált ikon-szöveg adatsorok.
              A teljes szekció rejtve marad, ha nincs sem PDF, sem adatsor. */}
          {(project.technicalPdf || project.technicalSpecs.length > 0) && (
            <div data-preview-section="technical" className="scroll-mt-24">
              <ScrollReveal>
                <TechnicalContent
                  pdf={project.technicalPdf}
                  specs={project.technicalSpecs}
                />
              </ScrollReveal>
            </div>
          )}

          {/* Unified building & floor-plan section: pick a floor → its plan
              with clickable apartments → details, open the unit page, or add
              to the compare set. Replaces the former two separate sections. */}
          {(floors.some((f) => f.floorPlanImageKey) ||
            sortedUnits.length > 0) && (
            <ScrollReveal>
              <BuildingSection
                floors={floors}
                units={sortedUnits}
                projectSlug={project.slug}
                pricesHidden={pricesHidden}
              />
            </ScrollReveal>
          )}

          {/* Hitelügyintézés — az épület-alaprajz és a lakáskereső között:
              itt már látta a látogató a kínálatot, de még nem szűkített, ez a
              természetes pont a „segítünk a finanszírozásban" felhívásnak. */}
          <ScrollReveal>
            <MortgageAdvisorBox variant="card" />
          </ScrollReveal>

          {/* Live unit filter — calculator-style */}
          {sortedUnits.length > 0 && (
            <ScrollReveal>
              <UnitFilter
                units={sortedUnits}
                projectSlug={project.slug}
                pricesHidden={pricesHidden}
              />
            </ScrollReveal>
          )}

          {/* Elérhető lakások — card gallery (cover image + key facts + Részletek) */}
          {sortedUnits.length > 0 && (
            <ScrollReveal>
              <span data-preview-section="units" className="block scroll-mt-24" />
              <h2 className="text-lg font-semibold uppercase tracking-widish text-brand-700 mb-5">
                Elérhető lakások
              </h2>
              <div className="grid sm:grid-cols-2 gap-4 lg:gap-6">
                {sortedUnits.map((u) => (
                  <UnitCard
                    key={u.id}
                    unit={u}
                    projectSlug={project.slug}
                    pricesHidden={pricesHidden}
                  />
                ))}
              </div>
            </ScrollReveal>
          )}

          {/* Units table + CTA buttons */}
          {sortedUnits.length > 0 && (
            <ScrollReveal>
              <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
                <h2 className="text-lg font-semibold uppercase tracking-widish text-brand-700">
                  Lakások ({sortedUnits.length})
                </h2>
                <div className="flex flex-wrap gap-2">
                  <Link
                    href={`/projektek/${project.slug}/muszaki-adatok`}
                    className="btn-secondary text-xs"
                  >
                    <KeyRound size={12} />
                    Részletes műszaki adatok
                  </Link>
                  <Link
                    href={`/projektek/${project.slug}/osszehasonlitas`}
                    className="btn-secondary text-xs"
                  >
                    <Layers size={12} />
                    Összehasonlítás
                  </Link>
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-brand-50 text-brand-500 text-[10px] uppercase tracking-widish font-semibold">
                      <tr>
                        <th className="text-left px-4 py-3">Lakás</th>
                        <th className="text-left px-4 py-3">Em.</th>
                        <th className="text-left px-4 py-3">Szoba</th>
                        <th className="text-left px-4 py-3">Méret</th>
                        <th className="text-left px-4 py-3">Ár</th>
                        <th className="text-left px-4 py-3">Státusz</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedUnits.map((u) => (
                        <UnitRow
                          key={u.id}
                          unit={u}
                          projectSlug={project.slug}
                          pricesHidden={pricesHidden}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </ScrollReveal>
          )}

          {/* Neighborhood — map → POI list → description */}
          {(project.locationAddress ||
            (project.locationLat && project.locationLng) ||
            project.neighborhoodPois.length > 0 ||
            project.neighborhoodDescription) && (
            <div data-preview-section="neighborhood" className="scroll-mt-24">
            <ScrollReveal>
              <Neighborhood
                address={project.locationAddress}
                lat={
                  project.locationLat != null
                    ? Number(project.locationLat)
                    : null
                }
                lng={
                  project.locationLng != null
                    ? Number(project.locationLng)
                    : null
                }
                pois={project.neighborhoodPois}
                description={project.neighborhoodDescription}
              />
            </ScrollReveal>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <ScrollReveal>
            <div className="card p-5 space-y-4">
              {project.locationAddress && (
                <div className="flex items-start gap-3">
                  <MapPin
                    size={18}
                    className="text-accent mt-0.5 shrink-0"
                  />
                  <div>
                    <div className="text-[10px] text-brand-400 uppercase tracking-widish font-semibold">
                      Helyszín
                    </div>
                    <div className="text-sm text-brand-700">
                      {project.locationAddress}
                    </div>
                  </div>
                </div>
              )}
              {project.expectedCompletion && (
                <div className="flex items-start gap-3">
                  <Calendar
                    size={18}
                    className="text-accent mt-0.5 shrink-0"
                  />
                  <div>
                    <div className="text-[10px] text-brand-400 uppercase tracking-widish font-semibold">
                      Várható átadás
                    </div>
                    <div className="text-sm text-brand-700">
                      {project.expectedCompletion}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="card p-5 mt-4">
              <h3 className="font-semibold uppercase tracking-widish text-xs text-brand-500 mb-3">
                Érdeklődés
              </h3>
              <DynamicForm projectId={project.id} compact />
            </div>
          </ScrollReveal>
        </aside>
      </div>
    </div>
  );
}

function TechnicalContent({
  pdf,
  specs,
}: {
  pdf: TechnicalPdf | null;
  specs: TechnicalSpec[];
}) {
  const sortedSpecs = [...specs].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
  const sizeLabel = pdf ? formatFileSize(pdf.size) : "";

  return (
    <div>
      <h2 className="text-lg font-semibold uppercase tracking-widish text-brand-700 mb-5">
        Műszaki tartalom
      </h2>

      {sortedSpecs.length > 0 && (
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-6">
          {sortedSpecs.map((spec, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-brand-700"
            >
              <span
                className="text-xl leading-6 shrink-0 w-7 text-center"
                aria-hidden="true"
              >
                {spec.icon || "•"}
              </span>
              <span className="text-sm leading-6 pt-0.5">{spec.text}</span>
            </li>
          ))}
        </ul>
      )}

      {pdf && (
        <a
          href={fileUrl(pdf.key)}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="card p-4 flex items-center gap-3 hover:bg-brand-50 transition-colors group max-w-md"
        >
          <FileText size={24} className="text-accent shrink-0" />
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-medium text-brand-700 group-hover:text-accent transition-colors truncate">
              {pdf.name || "Műszaki dokumentum"}
            </div>
            <div className="text-xs text-brand-400">
              PDF dokumentum{sizeLabel ? ` · ${sizeLabel}` : ""}
            </div>
          </div>
          <Download
            size={18}
            className="text-brand-400 group-hover:text-accent shrink-0"
          />
        </a>
      )}
    </div>
  );
}

function UnitCard({
  unit,
  projectSlug,
  pricesHidden,
}: {
  unit: ProjectUnit;
  projectSlug: string;
  pricesHidden: boolean;
}) {
  const cover = unit.gallery[0];
  const href = `/projektek/${projectSlug}/${unitSlug(unit)}`;

  return (
    <div className="card overflow-hidden flex flex-col group">
      {/* Cover image */}
      <Link
        href={href}
        className="relative block aspect-[4/3] bg-brand-200 overflow-hidden"
      >
        {cover ? (
          <img
            src={fileUrl(cover.key)}
            alt={cover.alt || unit.label}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full grid place-items-center bg-gradient-to-br from-brand-300 via-brand-400 to-brand-600 text-white/30">
            <Building2 size={72} strokeWidth={0.8} />
          </div>
        )}
        <span
          className={`absolute top-3 left-3 px-2 py-1 text-[10px] uppercase tracking-widish font-semibold ${UNIT_STATUS_BADGE[unit.status]}`}
        >
          {UNIT_STATUS_LABEL[unit.status]}
        </span>
      </Link>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading uppercase tracking-widish text-base text-brand-700">
          {unit.label}
        </h3>

        <dl className="mt-4 grid grid-cols-3 gap-2 text-sm">
          <div>
            <dt className="text-[10px] text-brand-400 uppercase tracking-widish font-semibold">
              Méret
            </dt>
            <dd className="text-brand-700 font-medium">
              {formatArea(unit.areaSqm as unknown as number | null)}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] text-brand-400 uppercase tracking-widish font-semibold">
              Szoba
            </dt>
            <dd className="text-brand-700 font-medium">{unit.rooms ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] text-brand-400 uppercase tracking-widish font-semibold">
              Em.
            </dt>
            <dd className="text-brand-700 font-medium">{unit.floor ?? "—"}</dd>
          </div>
        </dl>

        <div className="mt-auto pt-4 border-t border-brand-100 flex items-center justify-between gap-3">
          <div className="text-brand-700 font-semibold">
            {pricesHidden ? (
              <RequestQuoteLink href={unitQuoteHref(projectSlug, unit)} />
            ) : (
              formatPriceHuf(unitDisplayPriceHuf(unit))
            )}
          </div>
          <Link href={href} className="btn-primary text-xs shrink-0">
            Részletek
            <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function UnitRow({
  unit,
  projectSlug,
  pricesHidden,
}: {
  unit: ProjectUnit;
  projectSlug: string;
  pricesHidden: boolean;
}) {
  return (
    <tr className="border-t border-brand-100 hover:bg-brand-50/50 transition-colors">
      <td className="px-4 py-3 font-medium">
        <Link
          href={`/projektek/${projectSlug}/${unitSlug(unit)}`}
          className="text-brand-700 hover:text-accent transition-colors"
        >
          {unit.label}
        </Link>
      </td>
      <td className="px-4 py-3 text-brand-500">{unit.floor ?? "—"}</td>
      <td className="px-4 py-3 text-brand-500">{unit.rooms ?? "—"}</td>
      <td className="px-4 py-3 text-brand-500">
        {formatArea(unit.areaSqm as unknown as number | null)}
      </td>
      <td className="px-4 py-3 text-brand-700 font-medium">
        {pricesHidden ? (
          <RequestQuoteLink href={unitQuoteHref(projectSlug, unit)} />
        ) : (
          formatPriceHuf(unitDisplayPriceHuf(unit))
        )}
      </td>
      <td className="px-4 py-3">
        <span
          className={`px-2 py-1 text-[10px] uppercase tracking-widish font-semibold ${UNIT_STATUS_BADGE[unit.status]}`}
        >
          {UNIT_STATUS_LABEL[unit.status]}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <Link
          href={`/projektek/${projectSlug}/${unitSlug(unit)}`}
          className="inline-flex items-center gap-1.5 text-xs text-accent font-semibold uppercase tracking-widish hover:text-accent-700 transition-colors"
        >
          Megnézem
          <ArrowRight size={12} />
        </Link>
      </td>
    </tr>
  );
}
